<script setup lang="ts">
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import { useLocale } from '@/plugins/translation';
import { useRouter } from 'vue-router';
import { useUserContext } from '@ilteducation/ilt-auth';

const { t, l } = useLocale();
const router = useRouter();
const userContext = useUserContext();
const { smAndUp } = useDisplay();

const showQuizter = computed(() => userContext.hasRole('employee'));

type GameInfo = {
  title: string;
  description: string;
  icon: string;
  route: string;
  color: string;
  newTab: boolean;
  hideOnMobile?: boolean;
};

const baseGames: GameInfo[] = [
  {
    title: l.games.word_of_the_day.name,
    description: l.games.word_of_the_day.description,
    icon: 'fa:fas fa-seedling',
    route: '/games/word-of-the-day',
    color: 'success',
    newTab: false,
    hideOnMobile: true,
  },
  {
    title: l.games.the_human_body.title,
    description: l.games.the_human_body.description,
    icon: 'fa:fad fa-street-view',
    route: '/games/humanbody',
    color: 'primary',
    newTab: false,
    hideOnMobile: true,
  },
  {
    title: l.games.escape_room.title,
    description: l.games.escape_room.description,
    icon: 'fa:fad fa-key-skeleton',
    route: 'https://stonly.com/guide/sv/escape-room-fF4eWdbpFU/Steps/5686892',
    color: 'orange',
    newTab: true,
  },
  {
    title: l.games.planet_earth.title,
    description: l.games.planet_earth.description,
    icon: 'fa:fad fa-earth-europe',
    route: '/games/planetearth',
    color: 'blue',
    newTab: false,
    hideOnMobile: true,
  },
];

const quizterGame: GameInfo = {
  title: l.games.quizter.title,
  description: l.games.quizter.description,
  icon: 'fa:fad fa-seal-question',
  route: '/quizter/home',
  color: 'tertiary',
  newTab: false,
};

const games = computed(() => {
  if (showQuizter.value) {
    return [...baseGames, quizterGame];
  }
  return baseGames;
});

const navigateToGame = (game: GameInfo) => {
  if (game.newTab) {
    window.open(game.route, '_blank');
    return;
  }
  router.push(game.route);
};
</script>

<template>
  <v-container class="games-page pa-6">
    <h1 class="text-secondary mb-0 text-center">{{ t(l.games.title) }}</h1>

    <v-row class="mt-6" justify="center">
      <v-col
        v-for="game in games"
        :key="game.route"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        :class="{ 'd-none': !smAndUp && game.hideOnMobile }"
      >
        <v-card
          class="game-card h-100"
          :ripple="true"
          tabindex="0"
          role="button"
          @click="navigateToGame(game)"
          @keydown.enter="navigateToGame(game)"
          @keydown.space.prevent="navigateToGame(game)"
        >
          <v-card-text class="text-center pa-6 d-flex flex-column align-center">
            <v-avatar :color="game.color" size="80" class="mb-4">
              <v-icon :icon="game.icon" size="40" color="white"></v-icon>
            </v-avatar>
            <h2 class="text-h5 mb-2">{{ t(game.title) }}</h2>
            <p class="text-body-2 text-medium-emphasis">{{ t(game.description) }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
.games-page {
  max-width: 1200px;
  margin: 0 auto;
}

.game-card {
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s ease-in-out;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}
</style>
